import React, { useContext, useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Avatar } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes } from "../../data";
import { AuthContext } from "../../AuthContext";
import { Brands, Categories } from "./NavigationDropDown";
import ajaxService from "../../services/ajax-service";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PersonIcon from "@mui/icons-material/Person";

const NavigationBar = () => {
  const { logout, CartId } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isLoggedIn =
    localStorage.getItem("token") && localStorage.getItem("token") !== null;

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const handleLogout = async () => {
    ajaxService.get("/auth/logout", 0, true);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("expires_at");
    logout();
    navigate("/");
    setDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "black",
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        justifyContent: "space-between",
        // Tighter padding at md so items have more room
        px: { md: 1, lg: 3, xl: 8 },
        height: { md: 36, lg: 42, xl: 48 },
        // Prevent the bar itself from ever wrapping or clipping
        overflow: "visible",
        position: "relative",
      }}
    >
      {/* Left links — allow shrinking, clip with ellipsis before overflow */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          // Let items shrink at md; give them breathing room at lg+
          gap: { md: 0, lg: 0.5, xl: 1 },
          // Take up available space but yield to the right section
          flex: "1 1 auto",
          minWidth: 0,
          
        }}
      >
        {/* Home */}
        <Link
          to="/"
          key="home"
          style={{ textDecoration: "none", flexShrink: 0 }}
          className="flex items-center rounded-lg transition-all duration-300 hover:bg-white/20 hover:shadow-lg hover:scale-105 group"
        >
          <Typography
            className="poppins text-white transition-colors duration-300 group-hover:text-white/90"
            sx={{
              fontSize: { md: "0.7rem", lg: "0.85rem", xl: "1rem" },
              px: { md: 0.5, lg: 1 },
              py: 0.5,
              whiteSpace: "nowrap",
            }}
          >
            {t("navigation.home")}
          </Typography>
        </Link>

        <Divider />

        <Categories t={t} />

        <Divider />

        {routes.map((route, i) => {
          if (route.title === "brands")
            return (
              <React.Fragment key={i}>
                <Brands
                  title={t("navigation.brands")}
                
                />
                <Divider />
              </React.Fragment>
            );

          if (route.title === "best_deals") return null;

          return (
            <React.Fragment key={i}>
              <Link
                to={route.link}
                style={{ textDecoration: "none", flexShrink: 0 }}
                className="flex items-center rounded-lg transition-all duration-300 hover:bg-white/20 hover:shadow-lg hover:scale-105 group"
              >
                <Typography
                  className="poppins text-white transition-colors duration-300 group-hover:text-white/90"
                  sx={{
                    // Scale font down aggressively at md to prevent overflow
                    fontSize: { md: "0.65rem", lg: "0.8rem", xl: "0.95rem" },
                    px: { md: 0.5, lg: 1, xl: 1.5 },
                    py: { md: 0.4, lg: 0.6, xl: 0.75 },
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(
                    `navigation.${route.title.toLowerCase().replace(/\s+/g, "_")}`,
                    route.title
                  )}
                </Typography>
              </Link>
              <Divider />
            </React.Fragment>
          );
        })}
      </Box>

      {/* Right — Login/Signup OR User Avatar Dropdown — never shrinks */}
      <Box sx={{ flexShrink: 0, ml: { md: 0.5, lg: 1 } }}>
        {!isLoggedIn ? (
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "#2858A3",
              "&:hover": { bgcolor: "#1d4ed8" },
              textTransform: "none",
              px: { md: 1, lg: 1.5, xl: 2.5 },
              py: { md: 0.4, lg: 0.6, xl: 0.75 },
              fontSize: { md: "0.65rem", lg: "0.8rem", xl: "0.95rem" },
              whiteSpace: "nowrap",
              minWidth: "unset",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Link
                to="/user/login"
                style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
              >
                <Typography
                  className="poppins font-semibold text-white"
                  sx={{ fontSize: "inherit" }}
                >
                  {t("header.login")}
                </Typography>
              </Link>
              <Typography
                className="poppins text-white"
                sx={{ px: 0.25, fontSize: "inherit" }}
              >
                \
              </Typography>
              <Link
                to="/user/registration"
                style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
              >
                <Typography
                  className="poppins font-semibold text-white"
                  sx={{ fontSize: "inherit" }}
                >
                  {t("header.signup")}
                </Typography>
              </Link>
            </Box>
          </Button>
        ) : (
          <Box ref={dropdownRef} sx={{ position: "relative" }}>
            {/* Trigger */}
            <Box
              onClick={() => setDropdownOpen((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { md: 0.5, lg: 1 },
                cursor: "pointer",
                px: { md: 0.75, lg: 1.5 },
                py: { md: 0.4, lg: 0.75 },
                borderRadius: "8px",
                transition: "background 0.2s",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Avatar
                alt={user?.name}
                src={user?.avatar}
                sx={{
                  width: { md: 24, lg: 32, xl: 38 },
                  height: { md: 24, lg: 32, xl: 38 },
                  border: "2px solid #2858A3",
                  fontSize: { md: "0.65rem", lg: "0.875rem" },
                }}
              >
                {!user?.avatar && user?.name
                  ? user.name.charAt(0).toUpperCase()
                  : null}
              </Avatar>

              <Typography
                className="poppins font-semibold text-white"
                sx={{
                  // Hide the name at md to save space; show from lg up
                  display: { md: "none", lg: "block" },
                  fontSize: { lg: "0.75rem", xl: "0.875rem" },
                  whiteSpace: "nowrap",
                }}
              >
                {user?.name?.split(" ")[0] || t("header.dashboard")}
              </Typography>

              {/* Chevron */}
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                style={{
                  transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  flexShrink: 0,
                }}
              >
                <path
                  d="M2 4L6 8L10 4"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>

            {/* Dropdown panel */}
            {dropdownOpen && (
              <Box
                sx={{
                  position: "absolute",
                  right: 0,
                  mt: 1,
                  zIndex: 50,
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                  minWidth: { md: 180, lg: 210, xl: 240 },
                  overflow: "hidden",
                  border: "1px solid #e8eef6",
                }}
              >
                {/* User info header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { md: 1, lg: 1.5 },
                    px: { md: 2, lg: 3 },
                    py: { md: 2, lg: 2.5 },
                    borderBottom: "1px solid #f0f4fa",
                  }}
                >
                  <Avatar
                    alt={user?.name}
                    src={user?.avatar}
                    sx={{
                      width: { md: 34, lg: 40, xl: 48 },
                      height: { md: 34, lg: 40, xl: 48 },
                      border: "2px solid #2858A3",
                      fontSize: { md: "0.8rem", lg: "1rem" },
                    }}
                  >
                    {!user?.avatar && user?.name
                      ? user.name.charAt(0).toUpperCase()
                      : null}
                  </Avatar>
                  <Box>
                    <Typography
                      className="poppins font-semibold"
                      sx={{
                        color: "#1a1a2e",
                        lineHeight: 1.3,
                        fontSize: { md: "0.75rem", lg: "0.875rem" },
                      }}
                    >
                      {user?.name}
                    </Typography>
                    <Typography
                      className="poppins"
                      sx={{
                        color: "#888",
                        lineHeight: 1.4,
                        fontSize: { md: "0.65rem", lg: "0.75rem" },
                      }}
                    >
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>

                {/* Menu items */}
                <Box sx={{ py: 0.5 }}>
                  <Link
                    to="/user/dashboard"
                    onClick={() => setDropdownOpen(false)}
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { md: 1, lg: 1.5 },
                        px: { md: 2, lg: 2.5 },
                        py: { md: 1, lg: 1.5 },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": { bgcolor: "#f0f4fa" },
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: { md: 15, lg: 18 }, color: "#2858A3" }} />
                      <Typography
                        className="poppins font-semibold"
                        sx={{ color: "#1a1a2e", fontSize: { md: "0.75rem", lg: "0.875rem" } }}
                      >
                        {t("header.dashboard")}
                      </Typography>
                    </Box>
                  </Link>

                  <Link
                    to="/user/profile"
                    onClick={() => setDropdownOpen(false)}
                    style={{ textDecoration: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { md: 1, lg: 1.5 },
                        px: { md: 2, lg: 2.5 },
                        py: { md: 1, lg: 1.5 },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": { bgcolor: "#f0f4fa" },
                      }}
                    >
                      <PersonIcon sx={{ fontSize: { md: 15, lg: 18 }, color: "#2858A3" }} />
                      <Typography
                        className="poppins font-semibold"
                        sx={{ color: "#1a1a2e", fontSize: { md: "0.75rem", lg: "0.875rem" } }}
                      >
                        {t("header.profile", "Profile")}
                      </Typography>
                    </Box>
                  </Link>

                  <Box sx={{ height: "1px", bgcolor: "#f0f4fa", my: 0.5 }} />

                  <Box
                    onClick={handleLogout}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { md: 1, lg: 1.5 },
                      px: { md: 2, lg: 2.5 },
                      py: { md: 1, lg: 1.5 },
                      cursor: "pointer",
                      transition: "background 0.15s",
                      "&:hover": { bgcolor: "#fff0f0" },
                    }}
                  >
                    <PowerSettingsNewIcon
                      sx={{ fontSize: { md: 15, lg: 18 }, color: "#e10000" }}
                    />
                    <Typography
                      className="poppins font-semibold"
                      sx={{ color: "#e10000", fontSize: { md: "0.75rem", lg: "0.875rem" } }}
                    >
                      {t("header.logout")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Small helper so dividers aren't repeated inline
const Divider = () => (
  <Box
    sx={{
      flexShrink: 0,
      width: "1px",
      height: { md: 12, lg: 16, xl: 20 },
      bgcolor: "#393939",
      borderRadius: "25px",
      mx: { md: 0.25, lg: 0.5 },
    }}
  />
);

export default NavigationBar;